// Merchant Sales Report (MM-3037): lazy loading of the tree. One controller per (merchant, date range):
// changing either builds a new controller, which drops every cached page and ignores late responses of
// the old one.
import { TreeStore } from "./treeStore";
import { ChildrenPage, MerchantRow } from "./types";

export const CHILD_PAGE_SIZE = 20;

export type FetchChildren = (parentId: string, cursor: string | null) => Promise<ChildrenPage>;

export class ReportController {
  store = new TreeStore();
  private disposed = false;

  constructor(
    root: MerchantRow,
    private fetchChildren: FetchChildren,
    private callbacks: { onChange: () => void; onError: (message: string) => void },
  ) {
    this.store.setRoot(root);
  }

  /** Stops updates from requests that are still in flight (the page moved to another merchant or range). */
  dispose() {
    this.disposed = true;
  }

  /** Expand loads the first page once; collapsing and re-expanding never refetches. */
  async toggle(id: string) {
    const { store } = this;
    if (store.expanded.has(id)) {
      store.expanded.delete(id);
      this.callbacks.onChange();
      return;
    }
    store.expanded.add(id);
    const row = store.byId.get(id);
    if (!row || row.child_count === 0 || store.hasChildrenLoaded(id)) {
      this.callbacks.onChange();
      return;
    }
    await this.loadPage(id, null);
  }

  async loadMore(parentId: string) {
    const cursor = this.store.nextCursor.get(parentId);
    if (cursor) await this.loadPage(parentId, cursor);
  }

  private async loadPage(parentId: string, cursor: string | null) {
    const { store } = this;
    // One request per parent at a time: a double click must not load the same page twice.
    if (store.loading.has(parentId)) return;
    store.loading.add(parentId);
    this.callbacks.onChange();
    try {
      const page = await this.fetchChildren(parentId, cursor);
      if (this.disposed) return;
      store.appendChildren(parentId, page);
    } catch (error) {
      if (this.disposed) return;
      // Collapse back so clicking the chevron again retries.
      if (!store.hasChildrenLoaded(parentId)) store.expanded.delete(parentId);
      this.callbacks.onError(error instanceof Error ? error.message : "Unable to load merchants");
    } finally {
      if (!this.disposed) {
        store.loading.delete(parentId);
        this.callbacks.onChange();
      }
    }
  }
}
